/**
 * Finding a results column by its header, in ANY language the app speaks.
 *
 * The wizard creates a results table with headers in the user's language
 * ("Teléfono", "Resultado", "Monto comprometido"), and Voice writes each cell
 * by header (`Voice/domain/services/campaignResultColumns.ts`). Matching those
 * headers with the CURRENT language only broke two cases:
 *
 * - an existing table created in Spanish, picked with the app in English: every
 *   column reads as missing, the table is blocked as incompatible, and the
 *   campaign is saved without the columns Voice must fill;
 * - a draft created in Spanish and edited in English: the edit rebuilds those
 *   maps empty and Voice stops writing results.
 *
 * So a column is recognised by its header in en, es or pt, compared the way
 * Voice compares outcome labels (`foldOutcomeLabel`: no accents, no case,
 * whitespace collapsed) — looser than Voice's own header match (trimmed,
 * lowercase), which is safe because what is sent is the header AS THE TABLE
 * HAS IT, never the translation.
 *
 * Every string the table compares (a select's options, the "Resultado"
 * labels) must then be written in the language that column was found in:
 * `HeaderMatch.t` is that language's translator.
 */

import en from '../i18n/en.json';
import es from '../i18n/es.json';
import pt from '../i18n/pt.json';
import { foldOutcomeLabel } from './foldLabel';

export type Translate = (key: string) => string;

/** The languages a results table may have been created in. */
const BUNDLES = { en, es, pt } as const;

/** A translator over one language's campaigns bundle: the key itself when it has no string. */
function bundleTranslator(bundle: unknown): Translate {
  return (key) => {
    const found = key
      .split('.')
      .reduce<unknown>((acc, part) => (acc as Record<string, unknown> | undefined)?.[part], bundle);
    return typeof found === 'string' ? found : key;
  };
}

const LANGUAGES: Translate[] = Object.values(BUNDLES).map(bundleTranslator);

/** A column found in the table: its header there, and the language it was found in. */
export interface HeaderMatch {
  header: string;
  t: Translate;
}

/**
 * Finds each wanted column among the table's real headers.
 *
 * `wanted` pairs a key with the i18n key of its header. The languages are
 * tried in the order of how many of the wanted headers each one names in the
 * table — the table's own language first, the user's on a tie — so a column
 * the user added that happens to be named like a header in another language
 * (a Spanish table with its own "Status") is not taken for the real one.
 *
 * A column is never matched twice: two keys never share one header.
 */
export function matchHeaders(
  columnKeys: readonly string[] | undefined,
  wanted: ReadonlyArray<readonly [key: string, i18nKey: string]>,
  t: Translate,
): Map<string, HeaderMatch> {
  const columns = columnKeys ?? [];
  const byFold = new Map<string, string>();
  for (const header of columns) {
    const folded = foldOutcomeLabel(header);
    if (folded && !byFold.has(folded)) byFold.set(folded, header);
  }
  const exact = new Set(columns);
  const find = (candidate: string) =>
    exact.has(candidate) ? candidate : byFold.get(foldOutcomeLabel(candidate));

  const hits = (tr: Translate) => wanted.filter(([, i18nKey]) => find(tr(i18nKey))).length;
  // Stable sort: on a tie the user's language (first) stays first.
  const ranked = [t, ...LANGUAGES]
    .map((tr) => ({ tr, n: hits(tr) }))
    .sort((a, b) => b.n - a.n)
    .map(({ tr }) => tr);

  const claimed = new Set<string>();
  const found = new Map<string, HeaderMatch>();
  for (const [key, i18nKey] of wanted) {
    for (const tr of ranked) {
      const header = find(tr(i18nKey));
      if (header === undefined) continue;
      const folded = foldOutcomeLabel(header);
      if (claimed.has(folded)) continue;
      claimed.add(folded);
      found.set(key, { header, t: tr });
      break;
    }
  }
  return found;
}
